import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import EmailNotReceivedModal from "../../components/email-not-received-modal";
import { useAuth } from "../../context/authContext";
import { requestLinkEmailCode, verifyLinkEmail } from "../../api/auth";
import { getAuthApiError } from "../../utils/auth-api-error";
import { HelpLink } from "../../common/help";
import { usePlatform } from "../../hooks/usePlatform";
import styles from "./style.module.css";

const RESEND_COOLDOWN_SECONDS = 60;

type LinkEmailCodeLocationState = {
  email?: string;
};

function LinkEmailCodePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshMe } = useAuth();
  const { isTelegram } = usePlatform();
  const codeInputRef = useRef<HTMLInputElement>(null);
  const skipLinkedRedirectRef = useRef(false);

  const email = (location.state as LinkEmailCodeLocationState | null)?.email ?? "";

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  const [helpOpen, setHelpOpen] = useState(false);

  const emailLinked = useMemo(
    () => user?.linkedProviders.some((p) => p.provider === "email") ?? false,
    [user?.linkedProviders]
  );

  useEffect(() => {
    if (!email) {
      navigate("/account/login-methods/link-email", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (emailLinked && !skipLinkedRedirectRef.current) {
      navigate("/account/login-methods", { replace: true });
    }
  }, [emailLinked, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (code.length !== 6) return;

    let cancelled = false;
    (async () => {
      setError(null);
      setSubmitting(true);
      try {
        await verifyLinkEmail(email, code);
        if (cancelled) return;
        skipLinkedRedirectRef.current = true;
        await refreshMe();
        navigate("/account/login-methods/link-email/success", { replace: true });
      } catch (e) {
        if (!cancelled) {
          setError(getAuthApiError(e, "Не удалось подтвердить email."));
        }
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, email, navigate, refreshMe]);

  const resend = useCallback(async () => {
    if (resendIn > 0 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await requestLinkEmailCode(email);
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setCode("");
    } catch (e) {
      setError(getAuthApiError(e, "Не удалось переотправить код."));
    } finally {
      setSubmitting(false);
    }
  }, [email, resendIn, submitting]);

  const openSupport = useCallback(() => {
    setHelpOpen(false);
    window.open(HelpLink, "_blank", "noopener,noreferrer");
  }, []);

  if (!user || !email || (emailLinked && !skipLinkedRedirectRef.current)) return null;

  return (
    <div className={styles.page}>
      <h1 style={{ marginTop: isTelegram ? 0 : undefined }}>Введите код</h1>
      <p className={styles.subtitle}>Отправили его на {email}</p>

      <div className={styles.body}>
        <form
          className={styles.form}
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            ref={codeInputRef}
            className={styles.codeInput}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="••••••"
            maxLength={6}
            value={code}
            onChange={(e) => {
              setError(null);
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            }}
            disabled={submitting}
          />
          {error && <p className={styles.error}>{error}</p>}
        </form>

        <div className={styles.footer}>
          {resendIn > 0 ? (
            <p className={styles.resend}>Отправить еще раз через {resendIn}</p>
          ) : (
            <button
              type="button"
              className={styles.textButton}
              onClick={() => void resend()}
              disabled={submitting}
            >
              Отправить еще раз
            </button>
          )}
          <button
            type="button"
            className={styles.textButton}
            onClick={() => setHelpOpen(true)}
          >
            Не пришло письмо?
          </button>
        </div>
      </div>

      <EmailNotReceivedModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onSupport={openSupport}
      />
    </div>
  );
}

export default LinkEmailCodePage;
